import React, { useState, useEffect } from 'react';

export interface SetVisualizationParams {
  mode?: 'default' | '3d';
  color?: string;
  description?: string;
  customCode?: string;
  rippleIntensity?: number;
  glowIntensity?: number;
}

// Event for visualization updates
const VISUALIZATION_UPDATE_EVENT = 'visualization_updated';

// Custom event type
interface VisualizationUpdateEvent extends CustomEvent<SetVisualizationParams> {
  detail: SetVisualizationParams;
}

export type VisualizationMode = 'default' | '3d';

// Dispatch visualization update event
function dispatchVisualizationUpdate(params: SetVisualizationParams): void {
  const event = new CustomEvent(VISUALIZATION_UPDATE_EVENT, {
    detail: {
      mode: params.mode,
      color: params.color,
      description: params.description,
      customCode: params.customCode,
    }
  });
  window.dispatchEvent(event);
}

export const visualizationTools = [
  {
    schema: {
      name: 'set_visualization',
      description: `Updates the visualization appearance and behavior.`,
      parameters: {
        type: 'object',
        properties: {
          // mode: {
          //   type: 'string',
          //   enum: ['default', '3d'],
          //   description: 'Visualization mode - default (2D canvas) or 3D scene'
          // },
          color: {
            type: 'string',
            description: 'Hex color value for the visualization background (e.g., #FF0000). Use it to express different feelings in different situations.',
            pattern: '^#[0-9A-Fa-f]{6}$'
          },
          description: {
            type: 'string',
            description: 'Description of the desired visualization (for future use)'
          },
          customCode: {
            type: 'string',
            description: 'Custom code to render in the visualization (HTML/CSS/JS for default mode, Three.js code for 3D mode)'
          },
        }
      }
    },
    fn: async (params: SetVisualizationParams) => {
      // Validate color if provided
      if (params.color && !/^#[0-9A-Fa-f]{6}$/.test(params.color)) {
        throw new Error('Invalid color format. Must be a hex color (e.g., #FF0000)');
      }

      // Dispatch update event
      dispatchVisualizationUpdate(params);

      return `Visualization updated: ${JSON.stringify(params, null, 2)}`;
    }
  }
];

// Hook for subscribing to visualization updates
export function useVisualization(): SetVisualizationParams {
  const [visualizationState, setVisualizationState] = useState<SetVisualizationParams>({
    mode: 'default',
    color: '#000000',
    rippleIntensity: 1.5,
    glowIntensity: 3.0
  });

  useEffect(() => {
    const handleUpdate = (event: VisualizationUpdateEvent) => {
      setVisualizationState(prev => ({
        ...prev,
        ...event.detail
      }));
    };

    window.addEventListener(VISUALIZATION_UPDATE_EVENT, handleUpdate as EventListener);
    return () => {
      window.removeEventListener(VISUALIZATION_UPDATE_EVENT, handleUpdate as EventListener);
    };
  }, []);

  return visualizationState;
}

// Function to create visualization tools
export function createVisualizationTools(): typeof visualizationTools {
  return visualizationTools;
}


// set_visualization({
//   color: "#000000",
//   customCode: \`
//     <div id="container"></div>
//     <script src="https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js"></script>
//     <script>
//       const scene = new THREE.Scene();
//       const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
//       const renderer = new THREE.WebGLRenderer({ alpha: true });
//       renderer.setSize(window.innerWidth, window.innerHeight);
//       document.getElementById('container').appendChild(renderer.domElement);

//       const geometry = new THREE.BufferGeometry();
//       const particles = 5000;
//       const positions = new Float32Array(particles * 3);
//       const colors = new Float32Array(particles * 3);

//       for (let i = 0; i < positions.length; i += 3) {
//         positions[i] = (Math.random() - 0.5) * 10;
//         positions[i + 1] = (Math.random() - 0.5) * 10;
//         positions[i + 2] = (Math.random() - 0.5) * 10;
//         colors[i] = Math.random();
//         colors[i + 1] = Math.random();
//         colors[i + 2] = Math.random();
//       }

//       geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
//       geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

//       const material = new THREE.PointsMaterial({
//         size: 0.05,
//         vertexColors: true,
//         transparent: true
//       });

//       const points = new THREE.Points(geometry, material);
//       scene.add(points);
//       camera.position.z = 5;

//       function animate() {
//         requestAnimationFrame(animate);
        
//         if (window.audioData) {
//           const avg = window.audioData.reduce((a, b) => a + b) / window.audioData.length;
//           const scale = 1 + (avg / 255) * 0.5;
          
//           points.rotation.x += 0.001;
//           points.rotation.y += 0.002;
//           points.scale.set(scale, scale, scale);
//           material.size = 0.05 + (avg / 255) * 0.1;
//         }

//         renderer.render(scene, camera);
//       }

//       animate();

//       window.addEventListener('resize', () => {
//         camera.aspect = window.innerWidth / window.innerHeight;
//         camera.updateProjectionMatrix();
//         renderer.setSize(window.innerWidth, window.innerHeight);
//       });
//     </script>
//   \`
// })
